import type {
  PartitionFormat,
  PartitionSegmentType,
  PathSegment,
} from '@repo/api-gw-sdk';
import {
  BatchMode,
  ErrorPolicy,
  FileFormat,
  IngestionSourceType,
  KafkaKeyHandling,
  KafkaMessageFormat,
  KafkaSecurityProtocol,
  KafkaStartingOffset,
  SchemaEvolutionMode,
  SchemaRegistryAuthType,
  SegmentType,
} from '@repo/api-gw-sdk';

export interface PathSegmentState {
  id: string;
  segmentType: SegmentType;
  segmentStringValue: string;
  segmentPartitionName: string;
  segmentPartitionType: PartitionSegmentType;
  segmentPartitionFormat: PartitionFormat;
}

export interface IngestionPipelineWizardState {
  // Step 1: Pipeline Details
  name: string;
  namespace: string;
  tableName: string;

  // Step 2: Source Configuration
  sourceType: IngestionSourceType;
  // S3 fields
  s3Bucket: string;
  s3Region: string;
  s3OwnerAccountId: string;
  // GCS fields
  gcsBucket: string;
  gcsProjectId: string;
  gcsLocation: string;
  // Shared (S3/GCS)
  fileFormat: FileFormat;

  // Kafka Source fields
  kafkaBootstrapServers: string;
  kafkaTopic: string;
  kafkaConsumerGroupId: string;
  kafkaStartingOffset: KafkaStartingOffset;
  kafkaMessageFormat: KafkaMessageFormat;
  kafkaKeyHandling: KafkaKeyHandling;

  // Kafka Security fields
  kafkaSecurityProtocol: KafkaSecurityProtocol;
  kafkaSaslMechanism: string;
  kafkaCredentialsSecretArn: string;
  kafkaTlsSecretArn: string;
  kafkaSkipTlsVerification: boolean;

  // Schema Registry fields
  kafkaSchemaRegistryEnabled: boolean;
  kafkaSchemaRegistryUrl: string;
  kafkaSchemaRegistryAuthType: SchemaRegistryAuthType;
  kafkaSchemaRegistryCredentialsSecretArn: string;
  kafkaSchemaRegistryTlsSecretArn: string;
  kafkaSubjectNamingStrategy: string;
  kafkaSchemaEvolutionMode: SchemaEvolutionMode;

  // Kafka Partitioning, Batch, Error
  kafkaPartitioningColumns: string[];
  kafkaBatchMode: BatchMode;
  kafkaMaxRecordsPerBatch: string;
  kafkaMaxBatchIntervalSeconds: string;
  kafkaErrorPolicy: ErrorPolicy;
  kafkaMaxRetries: string;

  // Step 3: Partitioning
  partitionSpec: {
    separator: string;
    segments: PathSegmentState[];
  };

  // UI state
  partitionBuilderMode: 'build' | 'paste';
  samplePath: string;

  // UI state — existing pipeline names for duplicate validation
  existingPipelineNames: Set<string>;

  // Update mode — controls button text and success message in ReviewStep
  isUpdateMode: boolean;
}

export const defaultWizardState: IngestionPipelineWizardState = {
  // Step 1
  name: '',
  namespace: '',
  tableName: '',

  // Step 2
  sourceType: IngestionSourceType.INGESTION_SOURCE_TYPE_S3,
  s3Bucket: '',
  s3Region: '',
  s3OwnerAccountId: '',
  gcsBucket: '',
  gcsProjectId: '',
  gcsLocation: '',
  fileFormat: FileFormat.FILE_FORMAT_JSON,

  // Kafka
  kafkaBootstrapServers: '',
  kafkaTopic: '',
  kafkaConsumerGroupId: '',
  kafkaStartingOffset: KafkaStartingOffset.KAFKA_STARTING_OFFSET_EARLIEST,
  kafkaMessageFormat: KafkaMessageFormat.KAFKA_MESSAGE_FORMAT_JSON,
  kafkaKeyHandling: KafkaKeyHandling.KAFKA_KEY_HANDLING_IGNORE,
  kafkaSecurityProtocol:
    KafkaSecurityProtocol.KAFKA_SECURITY_PROTOCOL_PLAINTEXT,
  kafkaSaslMechanism: '',
  kafkaCredentialsSecretArn: '',
  kafkaTlsSecretArn: '',
  kafkaSkipTlsVerification: false,
  kafkaSchemaRegistryEnabled: false,
  kafkaSchemaRegistryUrl: '',
  kafkaSchemaRegistryAuthType:
    SchemaRegistryAuthType.SCHEMA_REGISTRY_AUTH_TYPE_NONE,
  kafkaSchemaRegistryCredentialsSecretArn: '',
  kafkaSchemaRegistryTlsSecretArn: '',
  kafkaSubjectNamingStrategy: '',
  kafkaSchemaEvolutionMode:
    SchemaEvolutionMode.SCHEMA_EVOLUTION_MODE_BACKWARD_COMPATIBLE,
  kafkaPartitioningColumns: [''],
  kafkaBatchMode: BatchMode.BATCH_MODE_MICRO_BATCH,
  kafkaMaxRecordsPerBatch: '',
  kafkaMaxBatchIntervalSeconds: '',
  kafkaErrorPolicy: ErrorPolicy.ERROR_POLICY_FAIL,
  kafkaMaxRetries: '3',

  // Step 3
  partitionSpec: {
    separator: '/',
    segments: [],
  },

  // UI state
  partitionBuilderMode: 'build',
  samplePath: '',

  existingPipelineNames: new Set<string>(),
  isUpdateMode: false,
};

export const mapSegments = (segments: PathSegmentState[]): PathSegment[] =>
  segments.map((seg) => ({
    segmentType: seg.segmentType,
    ...(seg.segmentType === SegmentType.SEGMENT_TYPE_STRING && {
      segmentStringValue: seg.segmentStringValue || undefined,
    }),
    ...(seg.segmentType === SegmentType.SEGMENT_TYPE_PARTITION && {
      segmentPartitionName: seg.segmentPartitionName || undefined,
      segmentPartitionType: seg.segmentPartitionType || undefined,
      segmentPartitionFormat: seg.segmentPartitionFormat || undefined,
    }),
  }));
